import { Button, Alert } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { Modal, Form, Container, Row, Col } from "react-bootstrap";

import {
  useCreateEarResponseMutation,
  useUpdateEarMutation,
  useUpdateEarResponseMutation,
} from "api/memberPortal/ear/ear.api";
import { handleAlertAndMailer } from "components/Products/Helper/functions";
import HTMLEditor from "components/UI/HTMLEditor";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

export default function ErtApproveRejectModal({
  show,
  handleClose,
  review,
  refetch,
  complete,
  selectedStatus,
}) {
  const [updatedResponse, setUpdatedResponse] = useState({
    note1: "",
  });
  const [error, setError] = useState(null);
  const { authToken, csrfToken } = useAuth();

  const [triggerUpdateEar, { isLoading: isUpdatingEar }] =
    useUpdateEarMutation();

  const [triggerCreateEarResponse, { isLoading: isCreatingEarResponse }] =
    useCreateEarResponseMutation();

  const [triggerUpdateEarResponse, { isLoading: isUpdatingEarResponse }] =
    useUpdateEarResponseMutation();

  const isLoading =
    isUpdatingEar || isUpdatingEarResponse || isCreatingEarResponse;

  const handleChange = (e) => {
    setUpdatedResponse({
      ...updatedResponse,
      [e.target.name]: e.target.value,
    });
  };

  const updateOrAddResponse = async ({ response, earid }) => {
    if (response !== null) {
      await triggerUpdateEarResponse({
        upid: response,
        ...updatedResponse,
      }).unwrap();
    } else {
      const newEarResponse = await triggerCreateEarResponse({
        earid: earid,
        pid: review.pid.product_id,
        note1: updatedResponse.note1,
      }).unwrap();

      await triggerUpdateEar({
        earid: earid,
        pid: review.pid.product_id,
        response: newEarResponse.upid,
      }).unwrap();
    }
  };

  //update ear status and note1
  const updateEar = async () => {
    setError(null);
    if (isLoading) return; // Prevent duplicate submissions
    try {
      const earResponse = await triggerUpdateEar({
        earid: review.earid,
        status: selectedStatus,
        gist: updatedResponse.note1,
        pid: review.pid.product_id,
        rejected: complete === false,
        approved: complete === true,
        enddate: getISOCurrentDate(),
      }).unwrap();

      await updateOrAddResponse({
        response: earResponse.response,
        earid: earResponse.earid,
      });

      await handleAlertAndMailer(csrfToken, authToken, {
        subject: `EAR Review ${earResponse.earid} for VID ${earResponse.pid} Status: ${earResponse.status}`,
        recipients: { to: ["Lab"], cc: ["NIAP Management", "Validators"] },
        alert_type: "EAR",
        alert_text: `EAR Review ${earResponse.earid} for VID ${earResponse.pid} Status: ${earResponse.status}`,
        alert_type_id: earResponse.earid,
      });
      refetch();
      handleClose();
    } catch (error) {
      console.log(error);
      setError("Error adding response to EAR, please try again.");
    }
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={() => {
        handleClose();
      }}
    >
      <Form>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <h3 className="text-bright-navy">
              {complete ? "COMPLETE EAR / ERT TEAM" : "REJECT EAR"}
            </h3>
            <h3 className="text-bright-navy">
              EAR ID REVIEW {review.earid} for VID {review?.pid?.v_id}
            </h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              <Col>
                <Row>
                  <Col>
                    <p className="fw-bold mb-0">Date of Receipt by the ERT:</p>
                    {review?.release_date != null ? (
                      <p>
                        {moment.utc(review?.release_date).format("MM/DD/YYYY")}
                      </p>
                    ) : (
                      <p>No Date Set</p>
                    )}
                  </Col>
                  <Col>
                    <p className="fw-bold mb-0">Completion Date:</p>
                    <p>{moment.utc(new Date()).format("MM/DD/YYYY")}</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="fw-bold mb-0">Deadline</p>
                    {review?.due != null ? (
                      <p>{moment.utc(review?.due).format("MM/DD/YYYY")}</p>
                    ) : (
                      <p>No Date Set</p>
                    )}
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <p className="fw-bold mb-0">
                      Official Response to the Lab:
                    </p>
                    {!complete && <p>(Rejection Rationale)</p>}
                    <HTMLEditor
                      name="note1"
                      handleChange={handleChange}
                      setIsValid={() => {}}
                      isValid={true}
                      defaultValue={review?.response?.note1}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer className="p-3">
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="outlined"
            type="button"
            onClick={() => {
              handleClose();
            }}
            disabled={isLoading}
            sx={{
              marginRight: "10px",
            }}
          >
            Cancel
          </Button>
          <Button
            loading={isLoading}
            variant="contained"
            color={complete ? "success" : "error"}
            onClick={updateEar}
          >
            {complete ? "Complete" : "Reject"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
